import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { submitLead, countryCodes, getDefaultCountryCode } from '@/services/api';

export const ContactFormDialog = ({ open, onOpenChange, source = 'popup', packageTitle = 'Essentials', packageAmount = 45, tmSignUpType }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    contact_number: '',
    country_code: '+1',
    message: '',
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    getDefaultCountryCode().then((code) => {
      setFormData((prev) => ({ ...prev, country_code: code }));
    });
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const result = await submitLead(formData, {
      source,
      pageUrl: window.location.href,
      packageTitle,
      packageAmount,
      tmSignUpType,
      onSuccess: (data) => {
        if (data.customerId) {
          window.location.href = `/thanks?customerid=${data.customerId}`;
        }
      },
    });

    if (result.success) {
      setFormData({
        name: '',
        email: '',
        contact_number: '',
        country_code: '+1',
        message: '',
      });
      onOpenChange(false);
    }

    setLoading(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]" data-testid="contact-form-dialog">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-[#06214c]" style={{ fontFamily: 'Playfair Display' }}>
            File Your Trademark Today
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="name">Name</Label>
            <Input
              id="name"
              placeholder="Enter your full name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              data-testid="contact-form-name"
            />
          </div>
          <div>
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              placeholder="your.email@example.com"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              required
              data-testid="contact-form-email"
            />
          </div>
          <div>
            <Label htmlFor="contact">Contact Number</Label>
            <div className="flex space-x-2">
              <Select
                value={formData.country_code}
                onValueChange={(value) => setFormData({ ...formData, country_code: value })}
              >
                <SelectTrigger className="w-[140px]" data-testid="contact-form-country-code">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {countryCodes.map((item) => (
                    <SelectItem key={item.code} value={item.code}>
                      {item.code} {item.country}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input
                id="contact"
                type="tel"
                placeholder="123-456-7890"
                value={formData.contact_number}
                onChange={(e) => {
                  const value = e.target.value.replace(/[^0-9]/g, '').slice(0, 10);
                  setFormData({ ...formData, contact_number: value });
                }}
                maxLength={10}
                pattern="[0-9]*"
                required
                className="flex-1"
                data-testid="contact-form-phone"
              />
            </div>
          </div>
          <div>
            <Label htmlFor="message">Message</Label>
            <Textarea
              id="message"
              placeholder="Tell us about your trademark needs..."
              value={formData.message}
              onChange={(e) => setFormData({ ...formData, message: e.target.value })}
              rows={4}
              required
              data-testid="contact-form-message"
            />
          </div>
          <Button
            type="submit"
            className="w-full btn-primary"
            disabled={loading}
            data-testid="contact-form-submit"
          >
            {loading ? 'Submitting...' : 'Register My Trademark'}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ContactFormDialog;